<template>
    <div class="rate-cards-viewer">
        <promo-ag-grid
            :row-data="rateCardsData"
            :column-defs="columnDefs"
            :grid-options="gridOptions"
            :grid-style="gridStyle"
            :default-col-def="defaultColDef"
            :single-click-edit="true"
            grid-class="ag-theme-custom__product-details"
            @grid-ready="onGridReady"
            @cell-value-changed="onCellValueChanged"
        />
        <div v-if="isNewRateCard" class="rate-cards-viewer__new-rate-card">
            <div class="rate-cards-viewer__new-rate-card-grid">
                <div class="rate-card-create__identifier">
                    <p class="rate-card-create__label">
                        {{ toSentenceCase($t('rateCards.headers.identifier')) }}
                    </p>
                    <vuex-text-field
                        vuex-module="rateCards"
                        :namespace="createNamespace"
                        field-name="identifier"
                        :validations="requiredValidations"
                        hide-details="auto"
                    />
                </div>
                <div class="rate-card-create__description">
                    <p class="rate-card-create__label">
                        {{ toSentenceCase($t('rateCards.headers.description')) }}
                    </p>
                    <vuex-text-field
                        vuex-module="rateCards"
                        :namespace="createNamespace"
                        field-name="description"
                        :validations="requiredValidations"
                        hide-details="auto"
                    />
                </div>
                <div class="rate-card-create__store-groups">
                    <p class="rate-card-create__label">
                        {{ toSentenceCase($t('rateCards.headers.storeGroups')) }}
                    </p>
                    <vuex-select
                        vuex-module="rateCards"
                        :namespace="createNamespace"
                        field-name="storeGroups"
                        :options="storeGroupsOptions"
                        item-text="description"
                        item-value="key"
                        multiple
                        chips
                        deletable-chips
                        return-object
                        autocomplete
                        finite-list
                        :max-items-in-list="3"
                        density="compact"
                        hide-details="auto"
                        :validate-on-blur="false"
                        :validations="requiredValidations"
                    />
                </div>
                <div class="rate-card-create__amount">
                    <p class="rate-card-create__label">
                        {{ $t('rateCards.headers.amount', { currency: currencySymbol }) }}
                    </p>
                    <vuex-currency-input
                        vuex-module="rateCards"
                        :namespace="createNamespace"
                        field-name="amount"
                        variant="underlined"
                        density="compact"
                        hide-details="auto"
                        :validations="amountValidations"
                    />
                </div>
                <div class="rate-card-create__period">
                    <p class="rate-card-create__label">
                        {{ toSentenceCase($t('rateCards.headers.period')) }}
                    </p>
                    <vuex-date-range
                        weeks-in-month="static"
                        vuex-module="rateCards"
                        :namespace="createNamespace"
                        :from="{ fieldName: 'effectiveFrom' }"
                        :to="{ fieldName: 'effectiveTo' }"
                        :min-date="createRateCardMinDate"
                    />
                </div>
                <div class="rate-card-create__actions">
                    <v-btn
                        variant="text"
                        size="small"
                        class="cancel-button"
                        @click="cancelCardRate"
                    >
                        {{ toSentenceCase($t('actions.cancel')) }}
                    </v-btn>
                    <simple-button :disabled="isSaveNewRateCardDisabled" @click="saveCardRate">
                        {{ toSentenceCase($t('actions.save')) }}
                    </simple-button>
                </div>
            </div>
        </div>
        <div class="grid-controls" :class="{ 'grid-controls--below': isNewRateCard }">
            <create-new-button
                :btn-text="toSentenceCase($tkey('rateCards.newRateCardButton'))"
                :disabled="isCreateDisabled"
                background
                @create-new="addNewRateCard"
            />
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { isNil } from 'lodash';
import { mapGetters, mapState, mapActions } from 'vuex';
import namespaces from '@enums/namespaces';
import severityEnums from '@enums/severity';
import storeGroupsOptionsMixin from '@/js/pages/mixins/store-groups-options-mixin';
import validators from '@/js/validators';
import RateCardActionsCell from './rate-card-actions-cell.vue';
import RateCardStoreGroupsCell from './rate-card-store-groups-cell.vue';
import RateCardPeriodCell from './rate-card-period-cell.vue';
import AgCurrencyInput from '@/js/components/promo-ag-grid/ag-currency-input.vue';
import agGridUtils from '@/js/utils/ag-grid-utils';
import { getPeriod } from '@/js/utils/supplier-commitment-utils';

export default {
    mixins: [storeGroupsOptionsMixin],
    data() {
        return {
            gridApi: null,
            isNewRateCard: false,
            gridOptions: markRaw({
                rowHeight: 68,
                headerHeight: 50,
                paginationAutoPageSize: true,
                pagination: true,
                getRowId: ({ data }) => String(data._id),
                rowClassRules: {
                    'grid__editing-row': params => this.isRowEditing(params.data?._id),
                },
                columnTypes: {
                    requiredField: agGridUtils.columnTypes.requiredField,
                },
            }),
            gridStyle: 'width: 100%; height: 100%;',
            defaultColDef: {
                suppressMovable: true,
                lockPinned: true,
                sortable: true,
                unSortIcon: true,
                flex: 1,
                resizable: true,
                filter: true,
                minWidth: 150,
                menuTabs: ['filterMenuTab'],
            },
        };
    },
    computed: {
        ...mapGetters('clientConfig', ['currencySymbol']),
        ...mapState('rateCards', ['rateCards', 'selectedRateCardId', 'stagingArea']),
        createNamespace() {
            return namespaces;
        },
        createRateCardModel() {
            return this.stagingArea[this.createNamespace] || {};
        },
        createRateCardMinDate() {
            return this.getRateCardMinDate({
                isNewRateCard: true,
                rateCard: null,
            });
        },
        isCreateDisabled() {
            return !isNil(this.selectedRateCardId) || this.isNewRateCard;
        },
        rateCardsData() {
            return this.rateCards;
        },
        isSaveNewRateCardDisabled() {
            return !this.isCreateRateCardValid();
        },
        requiredValidations() {
            return [{ validator: validators.required }];
        },
        amountValidations() {
            return [
                {
                    validator: validators.minValue,
                    params: [0, false],
                },
                {
                    validator: validators.required,
                },
            ];
        },
        columnDefs() {
            return [
                {
                    headerName: this.toSentenceCase(this.$t('rateCards.headers.identifier')),
                    field: 'identifier',
                    type: 'requiredField',
                    editable: params => this.isRowEditing(params.data._id),
                },
                {
                    headerName: this.toSentenceCase(this.$t('rateCards.headers.description')),
                    field: 'description',
                    type: 'requiredField',
                    editable: params => this.isRowEditing(params.data._id),
                },
                {
                    headerName: this.toSentenceCase(this.$t('rateCards.headers.storeGroups')),
                    field: 'storeGroups',
                    editable: false,
                    cellRenderer: RateCardStoreGroupsCell,
                    cellRendererParams: {
                        isEditing: this.isRowEditing,
                        onChange: this.onStoreGroupsChanged,
                        options: this.storeGroupsOptions,
                    },
                    filter: 'agSetColumnFilter',
                    filterValueGetter: params =>
                        (params.data.storeGroups || []).map(
                            ({ description, key }) => description || key
                        ),
                    minWidth: 260,
                },
                {
                    headerName: this.$t('rateCards.headers.amount', {
                        currency: this.currencySymbol,
                    }),
                    field: 'amount',
                    type: 'requiredField',
                    cellRenderer: AgCurrencyInput,
                    cellRendererParams: {
                        onInputChange: params => {
                            this.onCellValueChanged(params);
                        },
                        isDisabled: data => !this.isRowEditing(data._id),
                    },
                    filter: 'agNumberColumnFilter',
                    minWidth: 140,
                },
                {
                    headerName: this.toSentenceCase(this.$t('rateCards.headers.period')),
                    field: 'period',
                    editable: false,
                    cellRenderer: RateCardPeriodCell,
                    cellRendererParams: {
                        getMinDate: this.getRateCardMinDateValue,
                        getPeriod: this.getRateCardPeriod,
                        isEditing: this.isRowEditing,
                        onChange: this.onDateRangeChanged,
                    },
                    filterValueGetter: params => this.getRateCardPeriod(params.data),
                    minWidth: 300,
                },
                {
                    headerName: '',
                    cellRenderer: RateCardActionsCell,
                    cellRendererParams: {
                        isEditing: this.isRowEditing,
                        onEdit: this.startEditingRateCard,
                        onCancel: this.cancelEditingRateCard,
                        onDeleted: this.onRateCardDeleted,
                    },
                    editable: false,
                    sortable: false,
                    filter: false,
                    resizable: false,
                    minWidth: 110,
                    width: 110,
                    cellClass: 'flex-cell-center',
                },
            ];
        },
    },
    methods: {
        ...mapActions('notifications', ['addNotification']),
        ...mapActions('rateCards', [
            'resetStagingArea',
            'setSelectedRateCardId',
            'createRateCard',
            'updateRateCard',
        ]),
        onGridReady(params) {
            this.gridApi = params.api;
        },
        isRowEditing(rateCardId) {
            return this.selectedRateCardId === rateCardId;
        },
        getRateCardById(rateCardId) {
            return this.rateCards.find(rateCard => rateCard._id === rateCardId) || null;
        },
        startEditingRateCard(rateCard) {
            if (!rateCard?._id) {
                return;
            }

            this.setSelectedRateCardId(rateCard._id);

            if (this.gridApi) {
                this.gridApi.refreshCells({ force: true });
            }
        },
        cancelEditingRateCard() {
            this.setSelectedRateCardId(null);

            if (this.gridApi) {
                this.gridApi.refreshCells({ force: true });
            }
        },
        async onStoreGroupsChanged({ id, field, value }) {
            if (!value?.length) {
                this.restoreRateCardRow(id);
                await this.addNotification({
                    message: this.toSentenceCase(this.$t('validation.common.required')),
                    severity: severityEnums.error,
                });
                return;
            }

            await this.updateRateCard({
                id,
                updates: {
                    [field]: value,
                },
                refreshData: false,
            });
        },
        async onDateRangeChanged({ id, fields }) {
            if (!fields.effectiveFrom || !fields.effectiveTo) {
                return;
            }

            await this.updateRateCard({
                id,
                updates: fields,
                refreshData: false,
            });
        },
        async onCellValueChanged({ data, newValue, oldValue, colDef }) {
            const field = colDef?.field;

            if (!field || !data?._id || !this.isRowEditing(data._id) || newValue === oldValue) {
                return;
            }

            const value = typeof newValue === 'string' ? newValue.trim() : newValue;
            const validation = this.validateField({ field, value });

            if (!validation.isValid) {
                this.restoreRateCardRow(data._id);
                await this.addNotification({
                    message: validation.message,
                    severity: severityEnums.error,
                });
                return;
            }

            await this.updateRateCard({
                id: data._id,
                updates: {
                    [field]: value,
                },
                refreshData: false,
            });
        },
        getRateCardMinDateValue(rateCard) {
            return this.getRateCardMinDate({
                isNewRateCard: false,
                rateCard,
            });
        },
        getRateCardMinDate({ isNewRateCard, rateCard }) {
            // If the rate card is new or has not started yet, the start date cannot move into the past.
            const effectiveFrom = this.$moment(rateCard?.effectiveFrom);
            const rateCardStarted = effectiveFrom.isBefore(this.$moment(), 'day');

            if (isNewRateCard || !rateCardStarted) {
                return this.shortDate(this.$moment());
            }

            return undefined;
        },
        getRateCardPeriod(rateCard) {
            return getPeriod({
                startDate: rateCard.effectiveFrom,
                endDate: rateCard.effectiveTo,
                moment: this.$moment,
            });
        },
        formatStoreGroups(storeGroups = []) {
            return storeGroups.map(({ description, key }) => description || key).join(', ');
        },
        validateField({ field, value }) {
            if (field === 'identifier' || field === 'description') {
                const validation = validators.required(value);
                return {
                    isValid: validation === true,
                    message: validation === true ? null : validation,
                };
            }

            if (field === 'amount') {
                const requiredValidation = validators.required(String(value ?? ''));
                if (requiredValidation !== true) {
                    return {
                        isValid: false,
                        message: requiredValidation,
                    };
                }

                const minValidation = validators.minValue(value, undefined, 0, false);
                return {
                    isValid: minValidation === true,
                    message: minValidation === true ? null : minValidation,
                };
            }

            return {
                isValid: true,
                message: null,
            };
        },
        restoreRateCardRow(rateCardId) {
            const rateCard = this.getRateCardById(rateCardId);
            if (!rateCard || !this.gridApi) {
                return;
            }

            this.gridApi.applyTransaction({
                update: [{ ...rateCard }],
            });
        },
        onRateCardDeleted(rateCard) {
            if (this.selectedRateCardId === rateCard?._id) {
                this.cancelEditingRateCard();
            }
        },
        getCreateRateCardFirstError() {
            const rateCard = this.createRateCardModel;

            const identifierValidation = validators.required(rateCard.identifier);
            if (identifierValidation !== true) {
                return identifierValidation;
            }

            const descriptionValidation = validators.required(rateCard.description);
            if (descriptionValidation !== true) {
                return descriptionValidation;
            }

            const storeGroupsValidation = validators.required(rateCard.storeGroups);
            if (storeGroupsValidation !== true) {
                return storeGroupsValidation;
            }

            const amountRequiredValidation = validators.required(String(rateCard.amount ?? ''));
            if (amountRequiredValidation !== true) {
                return amountRequiredValidation;
            }

            const amountMinValidation = validators.minValue(rateCard.amount, undefined, 0, false);
            if (amountMinValidation !== true) {
                return amountMinValidation;
            }

            if (!rateCard.effectiveFrom || !rateCard.effectiveTo) {
                return this.toSentenceCase(this.$t('validation.supplierCommitment.datesRequired'));
            }

            return null;
        },
        isCreateRateCardValid() {
            return this.getCreateRateCardFirstError() === null;
        },
        validateCreateRateCard() {
            const firstError = this.getCreateRateCardFirstError();

            return {
                isValid: firstError === null,
                message: firstError,
            };
        },
        addNewRateCard() {
            this.isNewRateCard = true;
            this.resetStagingArea({ namespace: namespaces });
            this.setSelectedRateCardId(null);
        },
        cancelCardRate() {
            this.isNewRateCard = false;
            this.resetStagingArea({ namespace: namespaces });
        },
        async saveCardRate() {
            const validation = this.validateCreateRateCard();

            if (!validation.isValid) {
                await this.addNotification({
                    message: validation.message,
                    severity: severityEnums.error,
                });
                return;
            }

            const { error } = await this.createRateCard({
                document: { ...this.createRateCardModel },
                refreshData: false,
            });

            if (!error) {
                this.isNewRateCard = false;
                this.resetStagingArea({ namespace: namespaces });
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
@import '@style/base/_mixins.scss';

$rate-card-footer-height: 4.8rem;

.rate-cards-viewer {
    @include flex-column;
    height: 100%;
    padding-bottom: 3rem;
    background-color: $promo-white;

    .grid-controls {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        height: $rate-card-footer-height;
        box-sizing: border-box;
        font-size: 1.4rem;
        padding-left: 3rem;
        margin-top: -$rate-card-footer-height;
        position: relative;
        z-index: 1;
        pointer-events: none;

        > * {
            pointer-events: auto;
        }

        &--below {
            margin-top: 0;
            padding-top: 0.8rem;
            height: auto;
            pointer-events: auto;
        }
    }

    &__new-rate-card {
        padding: 1rem 3rem 2rem;
        border-top: $promo-divider-colour 0.1rem solid;

        &-grid {
            display: grid;
            align-items: start;
            width: 100%;
            column-gap: 1.2rem;
            row-gap: 0.8rem;
            grid-template-columns: 15fr 15fr 24fr 12fr 22fr 16rem;
        }

        &-grid > * {
            min-width: 0;
        }

        :deep(.rtls-text-field-container),
        :deep(.rtls-select-container),
        :deep(.rtls-calendar-picker),
        :deep(.vuex-currency-input) {
            min-width: 0;
            width: 100%;
        }

        .rate-card-create__identifier {
            grid-column: 1;
        }

        .rate-card-create__description {
            grid-column: 2;
        }

        .rate-card-create__store-groups {
            grid-column: 3;
            min-width: 0;
            overflow: hidden;
        }

        :deep(.rate-card-create__store-groups .v-autocomplete.v-input) {
            min-width: 0 !important;
            width: 100%;
        }

        :deep(.rate-card-create__store-groups .v-field) {
            min-width: 0;
        }

        :deep(.rate-card-create__store-groups .rtls-select .v-field__field),
        :deep(.rate-card-create__store-groups .rtls-select .v-field__input),
        :deep(.rate-card-create__store-groups .rtls-select .v-field__overlay) {
            min-height: 2.8rem;
        }

        :deep(.rate-card-create__store-groups .rtls-select .v-field__input) {
            align-items: center;
            padding-top: 0;
            padding-bottom: 0;
            min-width: 0;
            flex-wrap: wrap;
        }

        :deep(.rate-card-create__store-groups .rtls-select .v-autocomplete__selection) {
            margin-top: 0;
            margin-bottom: 0;
        }

        .rate-card-create__amount {
            grid-column: 4;
        }

        .rate-card-create__period {
            grid-column: 5;
        }

        :deep(.rate-card-create__amount .vuex-currency-input) {
            align-items: flex-start;
        }

        :deep(.rate-card-create__amount .vuex-currency-input__currency-symbol) {
            height: 2.8rem;
            min-height: 2.8rem;
            box-sizing: border-box;
        }

        .rate-card-create__actions {
            grid-column: 6;
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            align-items: center;
            flex-wrap: nowrap;
            gap: 0.8rem;
            min-width: 0;
            padding-top: 2rem;
            white-space: nowrap;
        }

        .rate-card-create__label {
            font-size: 1.2rem;
            line-height: 1.6rem;
            margin-bottom: 0.4rem;
            color: $promo-text-colour;
        }

        :deep(.rtls-calendar-picker) {
            padding: 0;
            margin: 0;
            max-width: none;
        }
    }

    .cancel-button {
        color: $promo-primary-colour;
        font-weight: 600;
    }

    :deep(.flex-cell-center) {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    :deep(.ag-center-cols-viewport) {
        overflow-x: hidden;
    }

    :deep(.grid__editing-row) {
        background-color: rgba(47, 71, 124, 0.05);
    }
}

:deep(.ag-theme-custom__product-details) {
    .ag-paging-panel {
        min-height: $rate-card-footer-height;
        height: $rate-card-footer-height;
    }

    .ag-paging-number,
    .ag-paging-row-summary-panel-number {
        font-weight: 700;
    }

    .ag-paging-button.ag-disabled .ag-icon {
        color: var(--ag-disabled-foreground-color, rgba(24, 29, 31, 0.5));
    }
}
</style>
